import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getSupabase } from '@/lib/supabase';
import { AUTH_GATE_ENABLED } from '@/lib/feature-flags';
import AuthGateScreen from './AuthGateScreen';

interface AuthGateProps {
  children: ReactNode;
  /** Presentation only; a public reader never receives protected children. */
  fallback?: ReactNode;
  /** A verified cold refresh may keep its reading presentation while loading. */
  loadingFallback?: ReactNode;
}

/**
 * App-wide HARD auth gate. Renders the full-screen sign-in gate
 * (AuthGateScreen, the tree-by-stream HomePrayerScene look) until a Supabase
 * session exists; renders the app (children) once authed. Sign-out clears the
 * session and the gate reappears automatically (every useAuth subscriber
 * re-renders). Plan: docs/plans/auth-gate/auth-gate-2026-06-25.md.
 *
 * Mounted INSIDE BrowserRouter + NavStackProvider (App.tsx) so the gate's
 * email AuthModal can register a `gate-auth` NavStack layer + host-owned
 * Escape (rule #78), mirroring AuthSection's `settings-auth` pattern.
 */
export default function AuthGate({ children, fallback, loadingFallback }: AuthGateProps) {
  const { user, loading } = useAuth();

  // Master switch + defensive fall-open: a hard gate with no auth backend
  // would be unreachable, so never brick local dev / a misconfigured deploy.
  // getSupabase() is a cached singleton getter (cheap, idempotent).
  if (!AUTH_GATE_ENABLED || getSupabase() == null) return <>{children}</>;

  if (loading) return loadingFallback !== undefined ? loadingFallback
    : fallback !== undefined ? fallback : <AuthGateScreen phase="loading" />;
  if (!user) return fallback !== undefined ? fallback : <AuthGateScreen phase="signin" />;

  // SOFT-GATE: to convert to a soft gate later, accept an onSkip prop +
  // a `skipped` flag (memory or localStorage) and return <>{children}</> when
  // skipped even though `user` is null, letting the app surface its own
  // register-nudge. v1 is a hard gate (no escape hatch).
  return <>{children}</>;
}
